/* BLUETTI Power — motion runtime.
 * Reveals the page as it scrolls in, the way the house reference site does.
 *
 * PROGRESSIVE BY CONSTRUCTION. The CSS rests VISIBLE; this script adds
 * html.bp-motion to opt the page into hidden-then-revealed, and only after it
 * has confirmed IntersectionObserver exists and the viewer has not asked for
 * reduced motion. No JS, an old browser, a crawler, or an error before this
 * runs: every .bp-reveal is simply visible.
 *
 * THE CHOREOGRAPHY (2026-09-30), channelled from the house reference site's house flagship page —
 * see motion.css section 1b. choreograph() gives every page its roles at load,
 * so no page's markup had to change:
 *
 *   stacks   the lines of a headline block (section heads, page headers, the
 *            copy on a photo band, each block of a Shopify
 *            product description) rise one after another, not as one slab.
 *   cards    anything revealing inside a .bp-reveal-group rises further.
 *   settle   card, shot and header photographs ease in from enlarged.
 *   frame    full-bleed photo bands open from a framed card to edge to edge,
 *            scrubbed by the scroll (the only scroll-driven part).
 *
 * The stagger follows ARRIVAL order: whatever enters the screen in the same
 * frame steps in 90ms apart, in reading order.
 *
 * Exposes window.BluettiPower.motion for manual control.
 */
(function () {
  "use strict";

  var reduced = window.matchMedia &&
                window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var capable = "IntersectionObserver" in window && !reduced;
  var root = document.documentElement;
  var observer = null;

  function each(list, fn) { for (var i = 0; i < list.length; i++) fn(list[i], i); }

  /* Things that move on their own terms: the pop-ups, the menus and the home
     page's hero slides each have their own entrance. */
  var OWN = ".bp-rent, .bp-mega, .bp-menu, .bp-herostack, .bp-show, .bp-cart-drawer";

  var STACKS = [
    ".bp-section-head",
    ".bp-hero__content",
    ".bp-media__copy",
    ".bp-description .bp-container > *"
  ].join(", ");

  var SETTLE = [
    ".bp-shot", ".bp-pcard__media", ".bp-card__photo", ".bp-film__shot",
    ".bp-figure", ".bp-bleed__media", ".bp-collage__lead", ".bp-vs__shot",
    ".bp-hero--media > .bp-media__bed"
  ].join(", ");

  var FRAME = "section.bp-media:not(.bp-hero), .bp-subscribe";

  function choreograph(scope) {
    // Stacks: the block stops moving as one piece; its lines move instead.
    each(scope.querySelectorAll(STACKS), function (box) {
      if (box.hasAttribute("data-bp-staged") || box.closest(OWN)) return;
      box.setAttribute("data-bp-staged", "");
      box.classList.remove("bp-reveal");
      each(box.children, function (el) {
        if (el.matches("script, style, template, [hidden]")) return;
        el.classList.add("bp-reveal", "bp-reveal--text");
      });
    });
    // Cards: whatever reveals inside a group rises from further down.
    each(scope.querySelectorAll(".bp-reveal-group .bp-reveal"), function (el) {
      if (!el.classList.contains("bp-reveal--text") && !el.closest(OWN)) {
        el.classList.add("bp-reveal--card");
      }
    });
    each(scope.querySelectorAll(SETTLE), function (el) {
      if (!el.closest(OWN)) el.classList.add("bp-settle");
    });
    each(scope.querySelectorAll(FRAME), function (el) {
      if (el.classList.contains("bp-cine")) return;
      el.classList.add("bp-cine");
      frames.push(el);
    });
  }

  function reveal(el) {
    el.classList.add("is-in");
  }

  /* Reading order: top to bottom, then left to right on the same line. */
  function byPosition(a, b) {
    var ra = a.boundingClientRect, rb = b.boundingClientRect;
    return Math.abs(ra.top - rb.top) > 8 ? ra.top - rb.top : ra.left - rb.left;
  }

  function start(scope) {
    scope = scope || document;
    if (capable) choreograph(scope);
    var nodes = scope.querySelectorAll(
      ".bp-reveal:not(.is-in), .bp-settle:not(.is-in)");
    if (!capable) {
      each(nodes, reveal);
      return;
    }
    root.classList.add("bp-motion");
    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        var hits = [];
        each(entries, function (e) { if (e.isIntersecting) hits.push(e); });
        hits.sort(byPosition);
        var step = 0;
        each(hits, function (e) {
          var el = e.target;
          // Photographs settle under their own card; they take no step.
          if (!el.classList.contains("bp-settle")) {
            el.style.setProperty("--bp-i", Math.min(step++, 8));
          }
          reveal(el);
          observer.unobserve(el);
        });
      }, { rootMargin: "0px 0px -10% 0px", threshold: 0.01 });
    }
    each(nodes, function (n) { observer.observe(n); });
    measure();
  }

  /* ---- FRAME: the one scroll-scrubbed effect ---------------------------
     --bp-p is 0 while the band's top is at the bottom of the screen and 1 by
     the time it has risen to a fifth of the way from the top, eased at both
     ends (smoothstep), and it runs backwards as you scroll back up. Only
     bands near the screen are measured, once a frame at most. */
  var frames = [];
  var queued = false;

  function measure() {
    queued = false;
    var vh = window.innerHeight || 800;
    for (var i = 0; i < frames.length; i++) {
      var el = frames[i];
      var r = el.getBoundingClientRect();
      if (r.bottom < -vh || r.top > vh * 1.5) continue;
      var p = (vh - r.top) / (vh * 0.8);
      p = p < 0 ? 0 : p > 1 ? 1 : p;
      p = p * p * (3 - 2 * p);
      var v = p.toFixed(3);
      if (el.__dpP !== v) { el.style.setProperty("--bp-p", v); el.__dpP = v; }
    }
  }

  function queue() {
    if (!queued && frames.length) {
      queued = true;
      window.requestAnimationFrame(measure);
    }
  }

  /* The sub-nav condenses once the hero is behind you. */
  function stickyCondense() {
    var bar = document.querySelector(".bp-subnav");
    if (!bar || !capable) return;
    var sentinel = document.querySelector(".bp-hero") || bar;
    new IntersectionObserver(function (e) {
      bar.classList.toggle("is-condensed", !e[0].isIntersecting);
    }, { threshold: 0 }).observe(sentinel);
  }

  function init() {
    start(document);
    stickyCondense();
    if (capable) {
      window.addEventListener("scroll", queue, { passive: true });
      window.addEventListener("resize", queue, { passive: true });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.BluettiPower = window.BluettiPower || {};
  window.BluettiPower.motion = {
    /* Re-scan after injecting markup. */
    scan: start,
    /* Show everything now, e.g. before printing. */
    revealAll: function () {
      each(document.querySelectorAll(".bp-reveal, .bp-settle"), reveal);
      each(frames, function (el) { el.style.setProperty("--bp-p", "1"); });
    },
    reduced: reduced
  };
})();
